import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import type { ModelChoice } from '@/types'

interface ModelContextValue {
  current: ModelChoice
  setModel: (choice: ModelChoice) => void
}

const ModelContext = createContext<ModelContextValue | null>(null)
// v2: bumped on 2026-05-16 — default changed van kimi-k2.6 naar gemma4:31b-cloud.
// Gemma is sneller (~500ms) en wordt onze Ollama-fallback wanneer Anthropic/Codex
// niet bereikbaar zijn. Later wordt Gemma ook de "router" die andere Ollama
// models aanstuurt (let op: free-tier Ollama Cloud kan maar 1 model tegelijk).
const STORAGE_KEY = 'tovieos-model-v2'

const DEFAULT: ModelChoice = {
  provider: 'ollama-cloud',
  model: 'gemma4:31b-cloud',
  display: 'gemma4 31b',
}

function readInitial(): ModelChoice {
  if (typeof window === 'undefined') return DEFAULT
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT
    const parsed = JSON.parse(raw) as Partial<ModelChoice>
    if (parsed.provider && parsed.model && parsed.display) {
      return parsed as ModelChoice
    }
  } catch {
    // bad JSON or storage blocked
  }
  return DEFAULT
}

export function ModelProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ModelChoice>(readInitial)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(current))
    } catch {
      // ignore
    }
  }, [current])

  const setModel = useCallback((choice: ModelChoice) => setCurrent(choice), [])

  return (
    <ModelContext.Provider value={{ current, setModel }}>
      {children}
    </ModelContext.Provider>
  )
}

export function useModel(): ModelContextValue {
  const ctx = useContext(ModelContext)
  if (!ctx) throw new Error('useModel must be used within ModelProvider')
  return ctx
}
